import { Controller } from "@hotwired/stimulus";

// Type definitions
interface StripeResponse {
  error?: { message: string };
  id?: string;
}

interface PaymentResponseData {
  type?: string;
  result?:
    | string
    | { message?: string; stripe_payment_intent?: { client_secret: string } };
  redirect_path?: string;
}

interface StripeInstance {
  StripeV3: (key: string) => StripeClient;
  newStripe: StripeClient;
  card: {
    createToken: (
      form: HTMLFormElement,
      callback: (status: number, response: StripeResponse) => void
    ) => void;
  };
}

interface StripeClient {
  handleCardPayment: (
    clientSecret: string
  ) => Promise<{ error?: { message: string } }>;
}

// Constants
const CONFIG = {
  POPUP_AUTO_CLOSE_DELAY: 8000,
  ENTER_KEY_CODE: 13,
  COMPANY_SELECTORS: [
    'input[name*="invoice"][value*="company"]:checked',
    'input[name*="payment"][value*="company"]:checked',
    'select[name*="payment"] option[value*="company"]:checked',
    'select[name*="invoice"] option[value*="company"]:checked',
    'input[type="checkbox"][name*="company"]:checked',
    'input[type="checkbox"][name*="invoice"]:checked',
  ],
  REQUIRED_FIELDS: [
    "address-organization-id",
    "address-company",
    "address-contact-person",
    "new_address_address1",
    "new_address_postal_code",
    "new_address_city",
  ],
} as const;

export default class OrderFormController extends Controller {
  static targets = [
    "submitBtn",
    "termsCheckbox",
    "errorBlock",
    "errorMessage",
    "loader",
  ];

  declare readonly submitBtnTarget: HTMLButtonElement;
  declare readonly termsCheckboxTarget: HTMLInputElement;
  declare readonly errorBlockTarget: HTMLElement;
  declare readonly errorMessageTarget: HTMLElement;
  declare readonly loaderTarget: HTMLElement;
  declare readonly hasSubmitBtnTarget: boolean;
  declare readonly hasTermsCheckboxTarget: boolean;
  declare readonly hasErrorBlockTarget: boolean;
  declare readonly hasErrorMessageTarget: boolean;
  declare readonly hasLoaderTarget: boolean;

  connect() {
    if (window.Stripe && window.gon?.stripe_key) {
      window.Stripe.newStripe = window.Stripe.StripeV3(window.gon.stripe_key);
    }
    this.updateSubmitButtonState();
  }

  termsClick() {
    this.updateSubmitButtonState();
  }

  async handleFormNext(event: Event) {
    event.preventDefault();
    this.setLoading(true);

    try {
      this.validateForm();
      // Submit the form to proceed to the next step
      const form = this.element as HTMLFormElement;
      form.submit();
    } catch (error) {
      this.showError(
        error instanceof Error
          ? error.message
          : "Please complete all required fields"
      );
      this.setLoading(false);
    }
  }

  async handleFormSubmit(event: Event) {
    event.preventDefault();
    this.setLoading(true);

    try {
      this.validateForm();
      await this.processPayment();
    } catch (error) {
      this.showError(
        error instanceof Error ? error.message : "Form submission failed"
      );
    }
  }

  checkCoupon(event: KeyboardEvent) {
    if (event.keyCode === CONFIG.ENTER_KEY_CODE) {
      (event.target as HTMLElement).parentElement
        ?.querySelector("button")
        ?.click();
    }
  }

  // Core functionality
  private validateForm() {
    if (!CONFIG.COMPANY_SELECTORS.some((sel) => document.querySelector(sel)))
      return;

    const missing = CONFIG.REQUIRED_FIELDS.find((id) => {
      const el = document.getElementById(id) as HTMLInputElement;
      return !el?.value.trim();
    });

    if (missing) {
      const name = missing
        .replace(/[-_]/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
      throw new Error(`${name} is required`);
    }
  }

  private async processPayment() {
    if (this.needsStripeToken()) {
      this.createStripeToken();
    } else {
      await this.submitForm();
    }
  }

  private needsStripeToken(): boolean {
    return ["stripe-payment-gateway", "add-new-credit-card"]
      .map((id) => document.getElementById(id))
      .concat([
        document.querySelector(
          'input[name="payment_gateway"][value="stripe"]:checked'
        ),
      ])
      .every((el) => el && !(el as HTMLElement).hidden);
  }

  private createStripeToken() {
    window.Stripe.card.createToken(
      this.element as HTMLFormElement,
      (_, response) => {
        if (response.error) return this.showError(response.error.message);
        if (!response.id)
          return this.showError("Invalid stripe token received");

        this.addTokenToForm(response.id);
        this.submitForm();
      }
    );
  }

  private addTokenToForm(token: string) {
    const select = document.getElementById(
      "payment_card_token"
    ) as HTMLSelectElement;
    if (select) {
      select.add(new Option(token, token));
      select.value = token;
    } else {
      const input = Object.assign(document.createElement("input"), {
        name: "payment[card_token]",
        value: token,
        type: "hidden",
      });
      this.element.appendChild(input);
    }
  }

  private async submitForm() {
    try {
      const response = await fetch((this.element as HTMLFormElement).action, {
        method: "POST",
        body: new FormData(this.element as HTMLFormElement),
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      const data: PaymentResponseData = await response.json();

      if (response.ok) {
        if (data.redirect_path) window.location.href = data.redirect_path;
      } else if (data.type === "requires_action") {
        await this.handleStripePayment(data);
      } else {
        this.resetCardSelection();
        this.showError(this.extractError(data));
      }
    } catch {
      this.showError("Network error occurred");
    }
  }

  private extractError(data: PaymentResponseData): string {
    if (typeof data.result === "string") return data.result;
    if (typeof data.result === "object" && data.result?.message)
      return data.result.message;
    return "Payment failed";
  }

  private async handleStripePayment(data: PaymentResponseData) {
    const clientSecret =
      typeof data.result === "object" &&
      data.result?.stripe_payment_intent?.client_secret;

    if (!clientSecret || !data.redirect_path) {
      return this.showError("Invalid payment data received");
    }

    try {
      const result = await window.Stripe.newStripe.handleCardPayment(
        clientSecret
      );
      if (result.error) {
        this.showError(result.error.message);
      } else {
        window.location.href = data.redirect_path;
      }
    } catch {
      this.showError("Payment processing failed");
    }
  }

  // UI Management
  private updateSubmitButtonState() {
    const enabled = this.hasTermsCheckboxTarget
      ? this.termsCheckboxTarget.checked
      : true;

    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = !enabled;
      this.submitBtnTarget.classList.toggle("cursor-not-allowed", !enabled);
      this.submitBtnTarget.classList.toggle("opacity-50", !enabled);
    }
  }

  private setLoading(loading: boolean) {
    if (loading) this.hideError();

    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled =
        loading ||
        !(this.hasTermsCheckboxTarget
          ? this.termsCheckboxTarget.checked
          : true);
    }
    if (this.hasLoaderTarget) {
      this.loaderTarget.hidden = !loading;
    }
  }

  private resetCardSelection() {
    const select = document.getElementById(
      "payment_card_token"
    ) as HTMLSelectElement;
    const form = document.getElementById("add-new-credit-card");

    if (form && !form.hidden && select) {
      select.value = "new_credit_card";
      const lastOption = select.lastElementChild as HTMLOptionElement;
      if (lastOption?.value !== "new_credit_card") {
        select.removeChild(lastOption);
      }
    }
  }

  // Error handling
  private showError(message: string) {
    if (this.hasErrorMessageTarget) this.errorMessageTarget.innerHTML = message;
    if (this.hasErrorBlockTarget) this.errorBlockTarget.hidden = false;

    this.createErrorPopup(message);
    this.setLoading(false);
  }

  private hideError() {
    if (this.hasErrorBlockTarget) this.errorBlockTarget.hidden = true;
    document.querySelector(".order-form-error-popup")?.remove();
  }

  private createErrorPopup(message: string) {
    document.querySelector(".order-form-error-popup")?.remove();

    const popup = document.createElement("div");
    popup.className = "order-form-error-popup";
    popup.style.cssText = `
      position: fixed; top: 20px; right: 20px; z-index: 99999; min-width: 300px; max-width: 500px;
      background: #f8d7da; color: #721c24; border: 1px solid #f5c6cb; border-left: 4px solid #dc3545;
      border-radius: 6px; padding: 15px 20px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      font-family: system-ui, sans-serif; font-size: 14px; opacity: 0; transform: translateX(100%);
      transition: all 0.3s ease-out;
    `;

    popup.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: flex-start;">
        <div style="flex: 1; margin-right: 10px;"><strong>Error:</strong> ${message}</div>
        <button type="button" class="close-btn" style="background: none; border: none; font-size: 20px; 
                font-weight: bold; color: #721c24; cursor: pointer; opacity: 0.8;">×</button>
      </div>
    `;

    const closePopup = () => {
      popup.style.opacity = "0";
      popup.style.transform = "translateX(100%)";
      setTimeout(() => popup.remove(), 300);
    };

    popup.querySelector(".close-btn")?.addEventListener("click", closePopup);
    setTimeout(closePopup, CONFIG.POPUP_AUTO_CLOSE_DELAY);

    document.body.appendChild(popup);
    setTimeout(() => {
      popup.style.opacity = "1";
      popup.style.transform = "translateX(0)";
    }, 10);
  }
}

declare global {
  interface Window {
    gon: { stripe_key: string };
    Stripe: StripeInstance;
  }
}
